import { useEffect } from 'react';
import { Button, Card, CardContent } from '@exam-prep/ui';
import { ErrorBox, Loading } from '../../components/Feedback';
import { type HomeProps, REFRESH_STAGES } from './model/grand-mock';
import { useGmLearner } from './hooks/useGmLearner';
import { useOnline } from './hooks/useOnline';
import { useNow } from './hooks/useNow';
import { useRemind } from './hooks/useRemind';
import { deriveStage, eligibilityHelp, streakOf } from './model/flow';
import { GM } from './model/copy';
import { GmHeader, GmFacts, OfflineNote } from './ui/GrandMockParts';
import {
  CancelledStage,
  EligibilityPanel,
  InProgressStage,
  MocksOff,
  NoGrandMock,
  PastResults,
  ReadyStage,
  ResultsStage,
  StatusLine,
  SubmittedStage,
  TeamNotices,
  UpcomingPanel,
} from './ui/GrandMockStages';

/** SSC Rankers League entry screen: the next exam, who can take part, and what to do next. */
/** Last exam's results, when they belong to an earlier exam than the one on screen. */
function pastResults<R extends { id: string }>(data: {
  grandMock: { id: string } | null;
  latestResults: R | null;
}): R | null {
  const last = data.latestResults;
  return last && last.id !== data.grandMock?.id ? last : null;
}

export function GrandMockHome({ onBack, onRules, onResume, onResults }: HomeProps) {
  const { data, offset, error, reload } = useGmLearner();
  const online = useOnline();
  const now = useNow(30_000);
  const reminder = useRemind(reload);

  const stage = deriveStage(data, now + offset);
  // Quietly refresh while waiting, so the screen moves on by itself at the start time.
  useEffect(() => {
    if (!REFRESH_STAGES.includes(stage.kind)) return;
    const id = setInterval(() => {
      if (!document.hidden) reload();
    }, 30_000);
    return () => clearInterval(id);
  }, [stage.kind, reload]);

  if (error && !data) return <ErrorBox error={error} retry={reload} />;
  if (!data) return <Loading />;
  if (!data.mocksEnabled) return <MocksOff onBack={onBack} />;
  const gm = data.grandMock;
  const me = data.me;

  const past = pastResults(data);
  const pastPanel = past ? <PastResults past={past} onResults={onResults} /> : null;

  if (!gm) return <NoGrandMock past={pastPanel} onBack={onBack} />;

  const k = stage.kind;
  return (
    <div className="space-y-4 p-4 pb-8">
      <Card className="live-card">
        <GmHeader gm={gm} />
        <CardContent className="space-y-3">
          <GmFacts gm={gm} />
          <p className="text-xs text-muted-foreground">{GM.schedule}</p>
        </CardContent>
      </Card>

      <OfflineNote online={online} />
      <TeamNotices notices={gm.notices} />

      {stage.kind === 'cancelled' ? <CancelledStage reason={stage.reason} /> : null}
      {stage.kind === 'in_progress' ? (
        <InProgressStage onResume={() => onResume(stage.attemptId)} />
      ) : null}
      {k === 'submitted' ? <SubmittedStage /> : null}
      {k === 'results' ? <ResultsStage onResults={() => onResults(gm.id)} /> : null}
      {pastPanel}
      {k === 'missed' ? <StatusLine text={GM.missed} /> : null}
      {k === 'paused' ? <StatusLine text={GM.paused} /> : null}
      {k === 'upcoming' || k === 'ready' || k === 'not_eligible' ? (
        <EligibilityPanel
          help={eligibilityHelp(me)}
          streak={streakOf(me)}
          me={me}
          notEligible={k === 'not_eligible'}
        />
      ) : null}
      {k === 'upcoming' && me?.eligibility.eligible ? (
        <UpcomingPanel
          msLeft={Date.parse(gm.startsAt) - (now + offset)}
          agreed={me.agreed}
          reminded={me.reminded || reminder.remindedNow}
          remindError={reminder.error}
          remindDisabled={reminder.busy || !online}
          onRemind={() => void reminder.remind(gm.id)}
          onRules={() => onRules(gm.id)}
        />
      ) : null}
      {k === 'ready' ? <ReadyStage online={online} onStart={() => onRules(gm.id)} /> : null}

      <Button variant="outline" size="lg" className="w-full" onClick={onBack}>
        {GM.back}
      </Button>
    </div>
  );
}
